<!--
  The saved Job Hazard Analyses.

  A list and a "New JHA" button, nothing else: the sheet itself is the form
  on the next screen (Task 5). Follows MixDesignList.svelte's structure.

  Per-site: with `?site=`, this is one site's JHAs, newest first — the
  grouped view lives on the site page (SiteForm.svelte) now, this screen is
  just its "All JHAs" drill-down. Without a site, every JHA across every
  site, flat, newest first.
-->
<script lang="ts">
  import { linkHandler, location, navigate, routePath, siteIdFromQuery } from '../../lib/router';
  import { jhaRecords, jhaRecordsLoading, loadJhaRecords } from '../../lib/store';

  const siteId = $derived(siteIdFromQuery($location.query));

  $effect(() => {
    void loadJhaRecords(siteId);
  });

  /** Newest by the record's own date, falling back to when it was last touched. */
  const sorted = $derived(
    [...$jhaRecords].sort((a, b) =>
      a.scheduled_start_date === b.scheduled_start_date
        ? b.updated_at.localeCompare(a.updated_at)
        : b.scheduled_start_date.localeCompare(a.scheduled_start_date)
    )
  );
</script>

<div class="card">
  <div class="card__body">
    {#if siteId !== null}
      <a class="btn btn--sm" href={routePath.qualitySite(siteId)} onclick={linkHandler}>&#8592; Back to site</a>
    {:else}
      <a class="btn btn--sm" href={routePath.quality()} onclick={linkHandler}>&#8592; All sites</a>
    {/if}
    <h1 class="card__title">Job Hazard Analysis</h1>
    <button
      class="btn btn--primary btn--block"
      type="button"
      onclick={() => navigate(routePath.newQualityJha(siteId ?? undefined))}
    >
      New JHA
    </button>
  </div>
</div>

{#if $jhaRecordsLoading && $jhaRecords.length === 0}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if sorted.length === 0}
  <div class="card">
    <div class="card__body">
      <p class="meta">No JHAs saved yet. The first one starts from the standard SCI sheet.</p>
    </div>
  </div>
{:else}
  <div class="card">
    <div class="card__body">
      <ul class="qlist">
        {#each sorted as record (record.id)}
          <li>
            <a href={routePath.qualityJha(record.id)} onclick={linkHandler}>
              <strong>{record.scheduled_start_date || 'No date recorded'}</strong>
              <span class="meta">{record.jobsite || 'No jobsite recorded'}</span>
              <span class="meta">{record.work_description || 'Untitled JHA'}</span>
              <span class="meta">{record.location || 'No location recorded'}</span>
            </a>
          </li>
        {/each}
      </ul>
    </div>
  </div>
{/if}

<style>
  .qlist {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .qlist li + li {
    border-top: 1px solid var(--line-soft);
  }

  .qlist a {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.85rem 0;
    color: inherit;
    text-decoration: none;
    /* Tapped with gloves on, same as the field screens. */
    min-height: var(--tap);
  }

  .qlist a:hover strong {
    text-decoration: underline;
  }
</style>
